import type { AxiosError } from "axios";
import { homeApi } from "@wkt/api/home/homeAPI";
import type {
  FullNewsDTO,
  FullSurveyDTO,
  FullUserMessageDTO,
  WeatherInfoDTO,
} from "@wkt/api/home/homeDTO";
import { useSessionCatalogStore } from "@wkt/stores/sessionCatalog";
import { defineStore } from "pinia";

type BridgeStatus = "idle" | "waiting" | "ready" | "error";

interface HomeState {
  messages: FullUserMessageDTO[];
  currentNews: FullNewsDTO[];
  currentSurveys: FullSurveyDTO[];
  weatherInfo: WeatherInfoDTO | null;
  bridgeStatus: BridgeStatus;
  hydrating: boolean;
  hydratedAt: string | null;
  lastError: string | null;
}

const BRIDGE_WAIT_INTERVAL_MS = 100;
const BRIDGE_WAIT_MAX_ATTEMPTS = 40;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function callApi<T>(
  runner: (
    onSuccess: (data: T) => void,
    onError: (error: AxiosError) => void,
  ) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    runner(resolve, reject);
  });
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return "Unable to refresh home content.";
}

function cloneMessage(message: FullUserMessageDTO): FullUserMessageDTO {
  return {
    ...message,
  };
}

export const useHomeStore = defineStore("home", {
  state: (): HomeState => ({
    messages: [],
    currentNews: [],
    currentSurveys: [],
    weatherInfo: null,
    bridgeStatus: "idle",
    hydrating: false,
    hydratedAt: null,
    lastError: null,
  }),

  getters: {
    isReady: (state) => state.bridgeStatus === "ready",
  },

  actions: {
    async ensureHydrated(): Promise<boolean> {
      if (this.bridgeStatus === "ready" && !this.hydrating) {
        if (
          this.messages.length > 0 ||
          this.currentNews.length > 0 ||
          this.currentSurveys.length > 0 ||
          this.weatherInfo != null
        ) {
          return true;
        }

        return true;
      }

      if (this.hydrating) {
        for (
          let attempt = 0;
          attempt < BRIDGE_WAIT_MAX_ATTEMPTS;
          attempt += 1
        ) {
          if (!this.hydrating) {
            return this.bridgeStatus === "ready";
          }

          await sleep(BRIDGE_WAIT_INTERVAL_MS);
        }

        return this.bridgeStatus === "ready";
      }

      this.hydrating = true;
      this.bridgeStatus = "waiting";
      this.lastError = null;

      const sessionCatalogStore = useSessionCatalogStore();
      const ready = await sessionCatalogStore.ensureSessionReady();

      if (!ready) {
        this.hydrating = false;
        this.bridgeStatus = "error";
        this.lastError = sessionCatalogStore.lastError;
        return false;
      }

      const employeeId = sessionCatalogStore.employee?.id;
      if (employeeId == null) {
        this.hydrating = false;
        this.bridgeStatus = "error";
        this.lastError = "Missing employee id for home bootstrap";
        return false;
      }

      try {
        const [messages, currentNews, currentSurveys, weatherInfo] =
          await Promise.all([
            callApi<FullUserMessageDTO[]>((onSuccess, onError) => {
              homeApi.getMessages(onSuccess, onError);
            }),
            callApi<FullNewsDTO[]>((onSuccess, onError) => {
              homeApi.getCurrentNews(employeeId, onSuccess, onError);
            }),
            callApi<FullSurveyDTO[]>((onSuccess, onError) => {
              homeApi.getCurrentSurveys(onSuccess, onError);
            }),
            callApi<WeatherInfoDTO>((onSuccess, onError) => {
              homeApi.getWeatherInfo(onSuccess, onError);
            }).catch(
              () => sessionCatalogStore.sessionInformation?.currentWeather ?? null,
            ),
          ]);

        this.messages = messages ?? [];
        this.currentNews = currentNews ?? [];
        this.currentSurveys = currentSurveys ?? [];
        this.weatherInfo = weatherInfo ?? null;
        this.bridgeStatus = "ready";
        this.hydratedAt = new Date().toISOString();
        this.lastError = null;
        return true;
      } catch (error) {
        this.bridgeStatus = "error";
        this.lastError = getErrorMessage(error);
        return false;
      } finally {
        this.hydrating = false;
      }
    },

    async refreshMessages(): Promise<boolean> {
      try {
        this.messages = await callApi<FullUserMessageDTO[]>(
          (onSuccess, onError) => {
            homeApi.getMessages(onSuccess, onError);
          },
        );
        this.lastError = null;
        this.hydratedAt = new Date().toISOString();
        return true;
      } catch (error) {
        this.lastError = getErrorMessage(error);
        return false;
      }
    },

    async deleteMessage(messageId: number): Promise<boolean> {
      try {
        await callApi<void>((onSuccess, onError) => {
          homeApi.deleteMessage(messageId, onSuccess, onError);
        });

        this.messages = this.messages.filter((message) => message.id !== messageId);
        this.lastError = null;
        this.hydratedAt = new Date().toISOString();
        return true;
      } catch (error) {
        this.lastError = getErrorMessage(error);
        return false;
      }
    },

    async respondMessage(
      message: FullUserMessageDTO,
      response: string,
    ): Promise<boolean> {
      try {
        const payload = cloneMessage(message);
        payload.response = response;
        payload.responseDate = new Date().toISOString();

        await callApi<void>((onSuccess, onError) => {
          homeApi.respondMessage(payload, onSuccess, onError);
        });

        this.messages = this.messages.map((current) =>
          current.id === payload.id ? { ...current, response, responseDate: payload.responseDate } : current,
        );
        this.lastError = null;
        this.hydratedAt = new Date().toISOString();
        return true;
      } catch (error) {
        this.lastError = getErrorMessage(error);
        return false;
      }
    },

    async refreshNews(): Promise<boolean> {
      const sessionCatalogStore = useSessionCatalogStore();
      const employeeId = sessionCatalogStore.employee?.id;
      if (employeeId == null) {
        return false;
      }

      try {
        this.currentNews = await callApi<FullNewsDTO[]>(
          (onSuccess, onError) => {
            homeApi.getCurrentNews(employeeId, onSuccess, onError);
          },
        );
        this.lastError = null;
        this.hydratedAt = new Date().toISOString();
        return true;
      } catch (error) {
        this.lastError = getErrorMessage(error);
        return false;
      }
    },

    async refreshSurveys(): Promise<boolean> {
      try {
        this.currentSurveys = await callApi<FullSurveyDTO[]>(
          (onSuccess, onError) => {
            homeApi.getCurrentSurveys(onSuccess, onError);
          },
        );
        this.lastError = null;
        this.hydratedAt = new Date().toISOString();
        return true;
      } catch (error) {
        this.lastError = getErrorMessage(error);
        return false;
      }
    },

    async refreshWeather(): Promise<boolean> {
      try {
        this.weatherInfo = await callApi<WeatherInfoDTO>(
          (onSuccess, onError) => {
            homeApi.getWeatherInfo(onSuccess, onError);
          },
        );
        this.lastError = null;
        this.hydratedAt = new Date().toISOString();
        return true;
      } catch (error) {
        this.lastError = getErrorMessage(error);
        return false;
      }
    },
  },
});
